import React, { lazy, Suspense, useEffect } from "react";
import axios from 'axios';

import Header from './Header';
import Footer from './Footer';

const NO_FOOTER_PATHS = ['/tim-kiem', '/search'];

function ConditionalFooter() {
  const { pathname } = useLocation();
  const hide = NO_FOOTER_PATHS.some(p => pathname.startsWith(p));
  return hide ? null : <Footer />;
}
import PreviewBanner from "./PreviewBanner";

import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function getSessionId() {
    let sid = sessionStorage.getItem('pn_sid');
    if (!sid) {
        sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
        sessionStorage.setItem('pn_sid', sid);
    }
    return sid;
}

function PageViewTracker() {
    const { pathname } = useLocation();
    useEffect(() => {
        const sid = getSessionId();
        fetch('/api/pageview/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sid, url: pathname, referrer: document.referrer }),
        }).catch(() => {});
    }, [pathname]);
    return null;
}
import { GOOGLE_CLIENT_ID } from './constants';

// Always eagerly loaded — on the critical path for every page
import LandingPage from "./LandingPage";

// Lazily loaded — only downloaded when the user navigates to that route
const EstateListPage            = lazy(() => import('./EstateListPage'));
const EstateDetail              = lazy(() => import('./EstateDetail'));
const Search                    = lazy(() => import('./Search'));
const RegisterEmailConfirm      = lazy(() => import('./RegisterEmailConfirm'));
const EmailVerifySuccess        = lazy(() => import('./EmailVerifySuccess'));
const ConstructionProjectDetail = lazy(() => import('./ConstructionProjectDetail'));
const ConstructionProjectList   = lazy(() => import('./ConstructionProjectList'));
const TermsAndConditions        = lazy(() => import('../../static/frontend/TermsAndConditions'));
const BrokerTerms               = lazy(() => import('../../static/frontend/BrokerTerms'));
const Complaints                = lazy(() => import('../../static/frontend/Complaints'));
const PrivacyPolicy             = lazy(() => import('../../static/frontend/PrivacyPolicy'));
const TechnicalSupport          = lazy(() => import('../../static/frontend/TechnicalSupport'));
const Contacts                  = lazy(() => import('../../static/frontend/Contacts'));
const ResetPasswordEmail        = lazy(() => import('./ResetPasswordEmail'));
const SetNewPassword            = lazy(() => import('./SetNewPassword'));
const InsertEstateForm          = lazy(() => import('./InsertEstateForm'));
const MapSample                 = lazy(() => import('./MapSample'));
const ProductTariff             = lazy(() => import('./ProductTariff'));
const CompanyForm               = lazy(() => import('./CompanyForm'));
const CompanyList               = lazy(() => import('./CompanyList'));
const CompanyDetail             = lazy(() => import('./CompanyDetail'));
const AgentForm                 = lazy(() => import('./AgentForm'));
const AgentList                 = lazy(() => import('./AgentList'));
const AgentDetail               = lazy(() => import('./AgentDetail'));
const WatchdogList              = lazy(() => import('./WatchdogList'));
const PartnerDashboard          = lazy(() => import('./PartnerDashboard'));
const CompanyServiceForm        = lazy(() => import('./CompanyServiceForm'));
const AdminEstateList           = lazy(() => import('./admin/AdminEstateList'));
const AdminEstateMessageList    = lazy(() => import('./admin/AdminEstateMessageList'));
const StatisticCharts           = lazy(() => import('./admin/StatisticCharts'));
const AdminTechnicalSupportList = lazy(() => import('./admin/AdminTechnicalSupportList'));
const AdminTechnicalSupportDetail = lazy(() => import('./admin/AdminTechnicaSupportDetail'));
const ServiceListPage             = lazy(() => import('./ServiceListPage'));
const ServiceDetailPage           = lazy(() => import('./ServiceDetailPage'));
const TranslatorListPage          = lazy(() => import('./TranslatorListPage'));
const MoneyTransferApplyPage      = lazy(() => import('./MoneyTransferApplyPage'));
const FavoritesPage               = lazy(() => import('./FavoritesPage'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <div className="spinner-border text-secondary" role="status" style={{ width: '2rem', height: '2rem' }} />
  </div>
);

class App extends React.Component {
	constructor(props) {
        super(props);
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('preview');
        const token = urlToken || sessionStorage.getItem('previewToken');
        if (token) {
            sessionStorage.setItem('previewToken', token);
            axios.defaults.headers.common['X-Preview-Token'] = token;
            if (urlToken) {
                sessionStorage.setItem('previewFirstVisit', '1');
            }
            params.delete('preview');
            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
            window.history.replaceState(null, '', newUrl);
        }
    }

  render() {
    return(
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Router>
        <ScrollToTop />
        <PageViewTracker />
        <Header />

        <PreviewBanner />
        <div style={{ flex: 1 }}>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route exact path="/" element={<LandingPage />} />
          <Route exact path="/search/" element={<Search />} />
          <Route exact path="/estate/list/" element={<EstateListPage />} />
          <Route exact path="/tim-kiem/" element={<EstateListPage />} />
          <Route exact path="/tim-kiem/:catSlug/" element={<EstateListPage />} />
          <Route exact path="/tim-kiem/:catSlug/:bizSlug/" element={<EstateListPage />} />
          <Route exact path="/tim-kiem/:catSlug/:bizSlug/:page/" element={<EstateListPage />} />
          <Route exact path="/register-email-confirm/" element={<RegisterEmailConfirm />} />
          <Route exact path="/auth/email-verify/" element={<EmailVerifySuccess />} />
          <Route exact path="/estate/list/:page/" element={<EstateListPage />} />
          <Route exact path="/estate/detail/:id/" element={<EstateDetail />} />
          <Route exact path="/can-ho/:slug/" element={<EstateDetail />} />
          <Route exact path="/nha/:slug/" element={<EstateDetail />} />
          <Route exact path="/dat/:slug/" element={<EstateDetail />} />
          <Route exact path="/thuong-mai/:slug/" element={<EstateDetail />} />
          <Route exact path="/khac/:slug/" element={<EstateDetail />} />
          <Route exact path="/construction-project/detail/:id/" element={<ConstructionProjectDetail />} />
          <Route exact path="/construction-project/list/:page/" element={<ConstructionProjectList />} />
          <Route exact path="/terms-and-conditions/" element={<TermsAndConditions />} />
          <Route exact path="/broker-terms/" element={<BrokerTerms />} />
          <Route exact path="/complaints/" element={<Complaints />} />
          <Route exact path="/privacy-policy/" element={<PrivacyPolicy />} />
          <Route exact path="/technical-support/" element={<TechnicalSupport />} />
          <Route exact path="/reset-password/" element={<ResetPasswordEmail />} />
          <Route exact path="/set-new-password/" element={<SetNewPassword />} />
          <Route exact path="/auth/estate/list/" element={<AdminEstateList />} />
          <Route exact path="/auth/message/list/" element={<AdminEstateMessageList />} />
          <Route exact path="/auth/create-estate/" element={<InsertEstateForm />} />
          <Route exact path="/partner/estate/edit/:editId/" element={<InsertEstateForm />} />
          <Route exact path="/auth/statistic-charts/" element={<StatisticCharts />} />
          <Route exact path="/auth/technical-support/list/" element={<AdminTechnicalSupportList />} />
          <Route exact path="/auth/technical-support/list/:page/" element={<AdminTechnicalSupportList />} />
          <Route exact path="/auth/technical-support/detail/:id/" element={<AdminTechnicalSupportDetail />} />
          <Route exact path="/geocode/" element={<MapSample />} />
          <Route exact path="/product/tariff/" element={<ProductTariff />} />
          <Route exact path="/contacts/" element={<Contacts />} />
          <Route exact path="/auth/watchdog/list/" element={<WatchdogList />} />
          <Route exact path="/oblibene/" element={<FavoritesPage />} />
          <Route exact path="/partner/dashboard/" element={<PartnerDashboard />} />
          <Route exact path="/company/service-edit/" element={<CompanyServiceForm />} />
          <Route exact path="/mortgage-service/" element={<ServiceListPage service="mortgage" />} />
          <Route exact path="/mortgage-service/:slug/" element={<ServiceDetailPage service="mortgage" />} />
          <Route exact path="/legal-service/" element={<ServiceListPage service="legal" />} />
          <Route exact path="/legal-service/:slug/" element={<ServiceDetailPage service="legal" />} />
          <Route exact path="/valuation-service/" element={<ServiceListPage service="valuation" />} />
          <Route exact path="/valuation-service/:slug/" element={<ServiceDetailPage service="valuation" />} />
          <Route exact path="/buyout-service/" element={<ServiceListPage service="buyout" />} />
          <Route exact path="/buyout-service/:slug/" element={<ServiceDetailPage service="buyout" />} />
          <Route exact path="/renovation-service/" element={<ServiceListPage service="renovation" />} />
          <Route exact path="/renovation-service/:slug/" element={<ServiceDetailPage service="renovation" />} />
          <Route exact path="/property-management/" element={<ServiceListPage service="management" />} />
          <Route exact path="/property-management/:slug/" element={<ServiceDetailPage service="management" />} />
          <Route exact path="/construction-service/" element={<ServiceListPage service="construction" />} />
          <Route exact path="/construction-service/:slug/" element={<ServiceDetailPage service="construction" />} />
          <Route exact path="/money-transfer/" element={<ServiceListPage service="money_transfer" />} />
          <Route exact path="/money-transfer/apply/" element={<MoneyTransferApplyPage />} />
          <Route exact path="/money-transfer/:slug/" element={<ServiceDetailPage service="money_transfer" />} />
          <Route exact path="/translation-service/" element={<TranslatorListPage />} />
        </Routes>
        </Suspense>
        </div>
        <ConditionalFooter />
      </Router>
      </div>
    );
  }
}

export default App;
export { DEFAULT_LANGUAGE, CZECH_LANGUAGE, DEFAULT_PASSWORD_TYPE, GOOGLE_CLIENT_ID, FACEBOOK_APP_ID, APPLE_CLIENT_ID, BANK_ACCOUNT, WEB_NAME } from './constants';
export const TECHNICAL_SUPPORT_EMAIL = 'info@prahanoi.cz';
export const COMPANY_NAME = 'PRAHANOI s.r.o.';
export const COMPANY_ADDRESS = 'Plotní 544/41, 602 00 Brno';
export const TEL = '+420 777 003 446';
export const MARKETING_EMAIL = 'info@prahanoi.cz';
    export const IC = '19524919';
export const DIC = 'CZ19524919';
